import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import './MortgageCalculator.css';

const emptyForm = {
  property_id: '',
  lender_name: '',
  payee_match_key: '',
  original_balance: '',
  current_balance: '',
  interest_rate: '',
  term_months: '360',
  start_date: '',
  monthly_pi: '',
  escrow_taxes: '',
  escrow_insurance: '',
  escrow_other: '',
  form_1098_interest: '',
  form_1098_year: String(new Date().getFullYear()),
  notes: ''
};

const fmt = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MortgageCalculator = () => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
  const [properties, setProperties] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [syncYear, setSyncYear] = useState(new Date().getFullYear());
  const [reviewForm, setReviewForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [propRes, mortRes] = await Promise.all([
        axios.get('/api/properties', { headers }),
        axios.get('/api/mortgage/profiles', { headers })
      ]);
      if (propRes.data.success) setProperties(propRes.data.data || []);
      if (mortRes.data.success) setProfiles(mortRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load mortgage data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const startEdit = (p) => {
    setEditingId(p.id);
    setSelectedId(p.id);
    setForm({
      property_id: String(p.property_id),
      lender_name: p.lender_name || '',
      payee_match_key: p.payee_match_key || '',
      original_balance: p.original_balance ?? '',
      current_balance: p.current_balance ?? '',
      interest_rate: p.interest_rate ?? '',
      term_months: p.term_months ?? 360,
      start_date: p.start_date || '',
      monthly_pi: p.monthly_pi ?? '',
      escrow_taxes: p.escrow_taxes ?? '',
      escrow_insurance: p.escrow_insurance ?? '',
      escrow_other: p.escrow_other ?? '',
      form_1098_interest: p.form_1098_interest ?? '',
      form_1098_year: p.form_1098_year ?? new Date().getFullYear(),
      notes: p.notes || ''
    });
    loadSchedule(p.id);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSchedule(null);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!form.property_id) {
      toast.error('Select a property');
      return;
    }
    try {
      const payload = {
        property_id: parseInt(form.property_id, 10),
        lender_name: form.lender_name || null,
        payee_match_key: form.payee_match_key || null,
        original_balance: form.original_balance === '' ? undefined : Number(form.original_balance),
        current_balance: form.current_balance === '' ? undefined : Number(form.current_balance),
        interest_rate: form.interest_rate === '' ? undefined : Number(form.interest_rate),
        term_months: parseInt(form.term_months, 10) || 360,
        start_date: form.start_date || null,
        monthly_pi: form.monthly_pi === '' ? null : Number(form.monthly_pi),
        escrow_taxes: Number(form.escrow_taxes) || 0,
        escrow_insurance: Number(form.escrow_insurance) || 0,
        escrow_other: Number(form.escrow_other) || 0,
        form_1098_interest: form.form_1098_interest === '' ? null : Number(form.form_1098_interest),
        form_1098_year: form.form_1098_year ? parseInt(form.form_1098_year, 10) : null,
        notes: form.notes || null
      };

      let res;
      if (editingId) {
        res = await axios.put(`/api/mortgage/profiles/${editingId}`, payload, { headers });
      } else {
        res = await axios.post('/api/mortgage/profiles', payload, { headers });
      }
      if (res.data.success) {
        toast.success(editingId ? 'Mortgage updated' : 'Mortgage profile saved');
        await load();
        const id = res.data.data.id;
        setEditingId(id);
        setSelectedId(id);
        loadSchedule(id);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
  };

  const loadSchedule = async (id) => {
    try {
      const res = await axios.get(`/api/mortgage/profiles/${id}/schedule?months=12`, { headers });
      if (res.data.success) setSchedule(res.data.data);
    } catch {
      toast.error('Failed to load schedule');
    }
  };

  const syncExpenses = async (id) => {
    if (!window.confirm(`Create estimated interest (+ escrow) expenses for ${syncYear}? Existing estimates for the same months are skipped.`)) return;
    try {
      const res = await axios.post(`/api/mortgage/profiles/${id}/sync-expenses`, {
        year: syncYear,
        include_escrow: true
      }, { headers });
      if (res.data.success) {
        toast.success(`Synced ${res.data.data.created_count} expense line(s). Reconcile to 1098 at year-end.`);
        loadSchedule(id);
      }
    } catch {
      toast.error('Failed to sync expenses');
    }
  };

  const openReview = (p) => {
    setReviewForm({
      id: p.id,
      current_balance: p.current_balance,
      interest_rate: p.interest_rate,
      monthly_pi: p.monthly_pi,
      escrow_taxes: p.escrow_taxes,
      escrow_insurance: p.escrow_insurance,
      escrow_other: p.escrow_other,
      notes: p.notes || ''
    });
  };

  const submitReview = async () => {
    try {
      const res = await axios.post(`/api/mortgage/profiles/${reviewForm.id}/review`, reviewForm, { headers });
      if (res.data.success) {
        toast.success(res.data.message || 'Review saved');
        setReviewForm(null);
        await load();
        loadSchedule(reviewForm.id);
      }
    } catch {
      toast.error('Failed to save review');
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this mortgage profile?')) return;
    try {
      await axios.delete(`/api/mortgage/profiles/${id}`, { headers });
      toast.success('Deactivated');
      if (selectedId === id) {
        setSelectedId(null);
        setSchedule(null);
        resetForm();
      }
      load();
    } catch {
      toast.error('Failed to deactivate');
    }
  };

  const reviewDue = profiles.filter(p => p.review_due);

  if (loading) {
    return <div className="mortgage-page"><p>Loading…</p></div>;
  }

  return (
    <div className="mortgage-page">
      <div className="mortgage-header">
        <div>
          <h1>Rental Mortgage Calculator</h1>
          <p className="mortgage-sub">
            Track P&amp;I vs escrow by property. Estimates change each month as principal drops.
            Reconcile annual interest to Form <strong>1098</strong> before filing.
          </p>
        </div>
        <Link to="/property-manager" className="mortgage-link-btn">Properties</Link>
      </div>

      {reviewDue.length > 0 && (
        <div className="mortgage-review-banner">
          <strong>{reviewDue.length} mortgage{reviewDue.length > 1 ? 's' : ''} need a check-in</strong>
          <span> Confirm rate, balance, and escrow every ~6 months.</span>
          <div className="mortgage-review-actions">
            {reviewDue.map(p => (
              <button key={p.id} type="button" onClick={() => openReview(p)}>
                Review {p.property_name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mortgage-grid">
        <section className="mortgage-card">
          <h2>{editingId ? 'Edit mortgage' : 'Add mortgage profile'}</h2>
          <form onSubmit={saveProfile} className="mortgage-form">
            <label>
              Property
              <select
                value={form.property_id}
                onChange={(e) => setField('property_id', e.target.value)}
                required
                disabled={!!editingId}
              >
                <option value="">Select rental / property…</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}{p.type === 'primary' ? ' (primary)' : ''}</option>
                ))}
              </select>
            </label>
            <label>
              Lender
              <input value={form.lender_name} onChange={(e) => setField('lender_name', e.target.value)} placeholder="Freedom Mortgage" />
            </label>
            <label>
              Bank statement match key
              <input
                value={form.payee_match_key}
                onChange={(e) => setField('payee_match_key', e.target.value)}
                placeholder="freedom mtg (optional — for statement linking)"
              />
            </label>
            <div className="mortgage-form-row">
              <label>
                Original balance
                <input type="number" step="0.01" value={form.original_balance} onChange={(e) => setField('original_balance', e.target.value)} />
              </label>
              <label>
                Current balance
                <input type="number" step="0.01" value={form.current_balance} onChange={(e) => setField('current_balance', e.target.value)} />
              </label>
            </div>
            <div className="mortgage-form-row">
              <label>
                Rate (% APR)
                <input type="number" step="0.001" value={form.interest_rate} onChange={(e) => setField('interest_rate', e.target.value)} />
              </label>
              <label>
                Term (months)
                <input type="number" value={form.term_months} onChange={(e) => setField('term_months', e.target.value)} />
              </label>
            </div>
            <div className="mortgage-form-row">
              <label>
                First payment / start
                <input type="date" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} />
              </label>
              <label>
                Monthly P&amp;I (leave blank to calculate)
                <input type="number" step="0.01" value={form.monthly_pi} onChange={(e) => setField('monthly_pi', e.target.value)} />
              </label>
            </div>
            <h3 className="mortgage-escrow-title">Monthly escrow (manual)</h3>
            <div className="mortgage-form-row three">
              <label>
                Property taxes
                <input type="number" step="0.01" value={form.escrow_taxes} onChange={(e) => setField('escrow_taxes', e.target.value)} />
              </label>
              <label>
                Insurance
                <input type="number" step="0.01" value={form.escrow_insurance} onChange={(e) => setField('escrow_insurance', e.target.value)} />
              </label>
              <label>
                Other
                <input type="number" step="0.01" value={form.escrow_other} onChange={(e) => setField('escrow_other', e.target.value)} />
              </label>
            </div>
            <div className="mortgage-form-row">
              <label>
                Form 1098 interest (optional)
                <input type="number" step="0.01" value={form.form_1098_interest} onChange={(e) => setField('form_1098_interest', e.target.value)} />
              </label>
              <label>
                1098 year
                <input type="number" value={form.form_1098_year} onChange={(e) => setField('form_1098_year', e.target.value)} />
              </label>
            </div>
            <label>
              Notes
              <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={2} />
            </label>
            <div className="mortgage-form-actions">
              <button type="submit" className="primary">{editingId ? 'Update profile' : 'Save profile'}</button>
              {editingId && (
                <button type="button" className="secondary" onClick={resetForm}>New profile</button>
              )}
            </div>
          </form>
        </section>

        <section className="mortgage-card">
          <h2>Your mortgages</h2>
          {profiles.length === 0 ? (
            <p className="mortgage-empty">No profiles yet. Add one for each rental with a loan.</p>
          ) : (
            <ul className="mortgage-list">
              {profiles.map(p => (
                <li key={p.id} className={selectedId === p.id ? 'active' : ''}>
                  <button type="button" className="mortgage-list-main" onClick={() => startEdit(p)}>
                    <span className="name">{p.property_name}</span>
                    <span className="meta">
                      {fmt(p.current_balance)} @ {p.interest_rate}% · P&amp;I {fmt(p.monthly_pi)}
                      {p.escrow_monthly > 0 ? ` · Escrow ${fmt(p.escrow_monthly)}` : ''}
                    </span>
                    {p.review_due && <span className="badge-due">Review due</span>}
                  </button>
                  <div className="mortgage-list-actions">
                    <button type="button" onClick={() => openReview(p)}>Check-in</button>
                    <button type="button" onClick={() => deactivate(p.id)}>Remove</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {schedule && (
        <section className="mortgage-card schedule-card">
          <div className="schedule-header">
            <div>
              <h2>12-month estimate — {schedule.profile.property_name}</h2>
              <p className="mortgage-sub">{schedule.note || 'Estimates only.'}</p>
            </div>
            <div className="schedule-sync">
              <label>
                Tax year
                <input type="number" value={syncYear} onChange={(e) => setSyncYear(parseInt(e.target.value, 10))} />
              </label>
              <button type="button" className="primary" onClick={() => syncExpenses(schedule.profile.id)}>
                Sync interest → expenses
              </button>
            </div>
          </div>
          <div className="schedule-totals">
            <div><span>Interest</span><strong>{fmt(schedule.schedule.totals.interest)}</strong></div>
            <div><span>Principal</span><strong>{fmt(schedule.schedule.totals.principal)}</strong></div>
            <div><span>Escrow</span><strong>{fmt(schedule.schedule.totals.escrow)}</strong></div>
            <div><span>Total paid</span><strong>{fmt(schedule.schedule.totals.payments)}</strong></div>
          </div>
          <div className="schedule-table-wrap">
            <table className="schedule-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Interest (tax)</th>
                  <th>Principal</th>
                  <th>Escrow</th>
                  <th>Total</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {schedule.schedule.months.map(m => (
                  <tr key={m.year_month}>
                    <td>{m.year_month}</td>
                    <td>{fmt(m.interest)}</td>
                    <td>{fmt(m.principal)}</td>
                    <td>{fmt(m.escrow)}</td>
                    <td>{fmt(m.total_payment)}</td>
                    <td>{fmt(m.ending_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {reviewForm && (
        <div className="mortgage-modal-backdrop" onClick={() => setReviewForm(null)}>
          <div className="mortgage-modal" onClick={(e) => e.stopPropagation()}>
            <h3>6-month mortgage check-in</h3>
            <p>Update anything that changed (rate, escrow, balance). Saves and sets the next reminder.</p>
            <div className="mortgage-form">
              <label>
                Current balance
                <input
                  type="number"
                  step="0.01"
                  value={reviewForm.current_balance}
                  onChange={(e) => setReviewForm({ ...reviewForm, current_balance: e.target.value })}
                />
              </label>
              <label>
                Rate (% APR)
                <input
                  type="number"
                  step="0.001"
                  value={reviewForm.interest_rate}
                  onChange={(e) => setReviewForm({ ...reviewForm, interest_rate: e.target.value })}
                />
              </label>
              <label>
                Monthly P&amp;I
                <input
                  type="number"
                  step="0.01"
                  value={reviewForm.monthly_pi}
                  onChange={(e) => setReviewForm({ ...reviewForm, monthly_pi: e.target.value })}
                />
              </label>
              <div className="mortgage-form-row three">
                <label>
                  Escrow taxes
                  <input
                    type="number"
                    step="0.01"
                    value={reviewForm.escrow_taxes}
                    onChange={(e) => setReviewForm({ ...reviewForm, escrow_taxes: e.target.value })}
                  />
                </label>
                <label>
                  Escrow insurance
                  <input
                    type="number"
                    step="0.01"
                    value={reviewForm.escrow_insurance}
                    onChange={(e) => setReviewForm({ ...reviewForm, escrow_insurance: e.target.value })}
                  />
                </label>
                <label>
                  Escrow other
                  <input
                    type="number"
                    step="0.01"
                    value={reviewForm.escrow_other}
                    onChange={(e) => setReviewForm({ ...reviewForm, escrow_other: e.target.value })}
                  />
                </label>
              </div>
              <label>
                Notes
                <textarea
                  rows={2}
                  value={reviewForm.notes}
                  onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })}
                />
              </label>
              <div className="mortgage-form-actions">
                <button type="button" className="secondary" onClick={() => setReviewForm(null)}>Cancel</button>
                <button type="button" className="primary" onClick={submitReview}>Confirm &amp; snooze 6 months</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MortgageCalculator;
